import React, { useState, useEffect } from 'react';
import { X, Send, MapPin, CheckCircle2, Trash2, Save, Navigation, Phone, Package } from 'lucide-react';
import { formatWhatsAppNumber } from '../../utils/phoneUtils';
import {
  DELIVERY_STATUS,
  setDeliveryAddress,
  markAddressRequested,
  markDelivered,
  deleteDelivery,
  updateDelivery,
} from '../../services/deliveryService';

const buildAddressRequestMessage = (firstName) => {
  const greeting = firstName ? `Merhaba ${firstName} bey/hanım,` : 'Merhaba,';
  return (
    `${greeting}\n\n` +
    `Kurbanınızı evinize teslim etmek üzere hazırlıyoruz. ` +
    `Lütfen teslimat adresinizi açık ve eksiksiz şekilde yazar mısınız? (Mahalle, sokak, bina no, daire no, ilçe/il)\n\n` +
    `— Hasımoğlu Çiftliği`
  );
};

const buildCenterPickupMessage = (firstName) => {
  const greeting = firstName ? `Sayın ${firstName},` : 'Sayın Hissedarımız,';
  const centerAddress = "Mevlana Cd. No:34, Sülüntepe, 34909 Pendik/İstanbul";
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("Mevlana Cd. No:34, Sülüntepe, 34909 Pendik/İstanbul")}`;
  
  return (
    `${greeting}\n\n` +
    `Kurbanınızı bayramın 1. günü aşağıdaki merkez vakıf adresimizden teslim alabilirsiniz.\n\n` +
    `📍 Vakıf Adresi: ${centerAddress}\n` +
    `🗺️ Konum: ${mapsLink}\n\n` +
    `Şimdiden hayırlı bayramlar dileriz.`
  );
};

export default function DeliveryDetailModal({ delivery, isOpen, onClose }) {
  const [message, setMessage] = useState('');
  const [address, setAddress] = useState('');
  const [note, setNote] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (delivery?.id) {
      const firstName = (delivery.customerName || '').split(' ')[0] || '';
      if (delivery.status === DELIVERY_STATUS.CENTER_PICKUP) {
        setMessage(buildCenterPickupMessage(firstName));
      } else {
        setMessage(buildAddressRequestMessage(firstName));
      }
      setAddress(delivery.address || '');
      setNote(delivery.note || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delivery?.id, delivery?.status]);

  if (!isOpen || !delivery) return null;

  const status = delivery.status || DELIVERY_STATUS.PENDING;
  const hasAddress = !!(delivery.address && delivery.address.trim());
  const isDelivered = status === DELIVERY_STATUS.DELIVERED;

  const handleSendWhatsApp = async () => {
    const waPhone = formatWhatsAppNumber(delivery.customerPhone);
    if (!waPhone) {
      alert('Geçerli bir telefon numarası bulunamadı.');
      return;
    }
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`, '_blank');
    if (status === DELIVERY_STATUS.PENDING) {
      try {
        await markAddressRequested(delivery.id);
      } catch (err) {
        console.error('Status update failed:', err);
      }
    }
  };

  const handleSaveAddress = async () => {
    setSavingAddress(true);
    try {
      await setDeliveryAddress(delivery.id, address);
    } catch (err) {
      alert('Adres kaydedilemedi: ' + err.message);
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      await updateDelivery(delivery.id, { note: (note || '').trim() });
    } catch (err) {
      alert('Not kaydedilemedi: ' + err.message);
    } finally {
      setSavingNote(false);
    }
  };

  const handleNavigate = () => {
    if (!hasAddress) return;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(delivery.address.trim())}`;
    window.open(url, '_blank');
  };

  const handleMarkDelivered = async () => {
    if (!window.confirm('Bu teslimatı "Teslim Edildi" olarak işaretlemek istediğinize emin misiniz?')) return;
    try {
      await markDelivered(delivery.id);
      onClose?.();
    } catch (err) {
      alert('Hata: ' + err.message);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Bu teslimat kaydını listeden ÇIKARMAK istediğinize emin misiniz? (Hissedar bilgileri silinmez, sadece teslimat kaydı silinir.)')) return;
    try {
      await deleteDelivery(delivery.id);
      onClose?.();
    } catch (err) {
      alert('Hata: ' + err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-navy-900 border border-navy-700/50 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="sticky top-0 z-10 flex items-center justify-between p-5 bg-navy-900 border-b border-navy-700/50">
          <div>
            <h2 className="text-lg font-bold text-white">Teslimat Detayı</h2>
            <p className="text-xs text-navy-400 mt-0.5">Adres iste, kaydet ve yön tarifi al.</p>
          </div>
          <button onClick={onClose} className="p-2 text-navy-400 hover:text-white rounded-lg hover:bg-navy-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="bg-navy-800 border border-navy-700/50 rounded-xl p-4">
            <div className="text-white font-bold text-base">{delivery.customerName}</div>
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-navy-300">
              <span className="inline-flex items-center gap-1">
                <Phone className="w-3.5 h-3.5" />
                <span className="font-mono">{delivery.customerPhone}</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <Package className="w-3.5 h-3.5" />
                {Number(delivery.shareCount) || 0} hisse
              </span>
            </div>
          </div>

          <div className="bg-navy-800 border border-navy-700/50 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4 text-forest-400" />
              <h3 className="text-white text-sm font-bold">WhatsApp Adres İste</h3>
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="w-full bg-navy-950 border border-navy-700 rounded-lg px-3 py-2 text-sm text-white placeholder-navy-500 resize-none"
              placeholder="WhatsApp mesajı..."
            />
            <button
              onClick={handleSendWhatsApp}
              disabled={isDelivered}
              className="w-full py-2.5 bg-forest-600 hover:bg-forest-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-lg shadow-forest-500/20"
            >
              <Send className="w-4 h-4" />
              WhatsApp ile Gönder
            </button>
          </div>

          <div className="bg-navy-800 border border-navy-700/50 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-400" />
              <h3 className="text-white text-sm font-bold">Teslimat Adresi</h3>
            </div>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={4}
              className="w-full bg-navy-950 border border-navy-700 rounded-lg px-3 py-2 text-sm text-white placeholder-navy-500 resize-none"
              placeholder="Müşterinin WhatsApp'tan gönderdiği adresi buraya yapıştırın..."
            />
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleSaveAddress}
                disabled={savingAddress}
                className="flex-1 min-w-[140px] py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-white font-bold text-sm flex items-center justify-center gap-2 transition-all"
              >
                <Save className="w-4 h-4" />
                {savingAddress ? 'Kaydediliyor...' : 'Adresi Kaydet'}
              </button>
              <button
                onClick={handleNavigate}
                disabled={!hasAddress}
                className="flex-1 min-w-[140px] py-2 bg-navy-700 hover:bg-navy-600 disabled:opacity-40 disabled:cursor-not-allowed border border-navy-600 rounded-lg text-white font-bold text-sm flex items-center justify-center gap-2 transition-all"
              >
                <Navigation className="w-4 h-4" />
                Bu Adrese Git
              </button>
            </div>
          </div>

          <div className="bg-navy-800 border border-navy-700/50 rounded-xl p-4 space-y-3">
            <h3 className="text-white text-sm font-bold">Not (opsiyonel)</h3>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="w-full bg-navy-950 border border-navy-700 rounded-lg px-3 py-2 text-sm text-white placeholder-navy-500 resize-none"
              placeholder="Kapı kodu, kat bilgisi, teslim saati vb."
            />
            <button
              onClick={handleSaveNote}
              disabled={savingNote}
              className="px-4 py-1.5 bg-navy-700 hover:bg-navy-600 disabled:opacity-50 border border-navy-600 rounded-lg text-white text-xs font-bold inline-flex items-center gap-2"
            >
              <Save className="w-3.5 h-3.5" />
              {savingNote ? 'Kaydediliyor...' : 'Notu Kaydet'}
            </button>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-navy-700/50">
            <button
              onClick={handleMarkDelivered}
              disabled={isDelivered || !hasAddress}
              className="flex-1 min-w-[140px] py-2.5 bg-forest-600/20 text-forest-300 border border-forest-500/30 hover:bg-forest-500 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-forest-600/20 disabled:hover:text-forest-300 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-all"
            >
              <CheckCircle2 className="w-4 h-4" />
              {isDelivered ? 'Teslim Edildi' : 'Teslim Edildi Olarak İşaretle'}
            </button>
            <button
              onClick={handleDelete}
              className="py-2.5 px-4 bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500 hover:text-white rounded-lg font-bold text-sm flex items-center gap-2 transition-all"
            >
              <Trash2 className="w-4 h-4" />
              Listeden Çıkar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
